import { create } from 'zustand'
import { chatApi } from '@/api/chat'
import { getErrorMessage } from '@/utils/error'
import type { ChatContact, ChatMessage, ChatRoom, SendMessagePayload } from '@/api/chat'

interface RoomMessagesState {
  messages: ChatMessage[]
  hasMore: boolean
  nextBeforeId: number | null
  loaded: boolean
}

interface ChatState {
  rooms: ChatRoom[]
  contacts: ChatContact[]
  currentRoomId: number | null
  byRoom: Record<number, RoomMessagesState>
  isLoadingRooms: boolean
  isLoadingMessages: boolean
  isLoadingContacts: boolean
  error: string | null

  fetchRooms: () => Promise<void>
  fetchContacts: () => Promise<void>
  openDirect: (userId: number) => Promise<ChatRoom>
  setCurrentRoom: (roomId: number | null) => void
  fetchMessages: (roomId: number, options?: { loadMore?: boolean }) => Promise<void>
  sendMessage: (roomId: number, payload: SendMessagePayload) => Promise<ChatMessage>
  editMessage: (roomId: number, messageId: number, payload: SendMessagePayload) => Promise<void>
  deleteMessage: (roomId: number, messageId: number) => Promise<void>
  markRead: (roomId: number, upTo: number) => Promise<void>
  setRoomMuted: (roomId: number, isMuted: boolean) => void
  updateRoom: (room: ChatRoom) => void

  applyIncomingMessage: (message: ChatMessage) => void
  applyUpdatedMessage: (message: ChatMessage) => void
  applyDeletedMessage: (message: ChatMessage) => void
  applyReadMark: (roomId: number, userId: number, lastReadMessageId: number) => void

  clearError: () => void
}

export const selectHasUnreadMessages = (s: ChatState): boolean =>
  s.rooms.some((r) => r.unread_count > 0)

const emptyRoomState: RoomMessagesState = {
  messages: [],
  hasMore: false,
  nextBeforeId: null,
  loaded: false,
}

function upsertRoom(rooms: ChatRoom[], room: ChatRoom): ChatRoom[] {
  const exists = rooms.some((r) => r.id === room.id)
  return exists ? rooms.map((r) => (r.id === room.id ? room : r)) : [room, ...rooms]
}

export const useChatStore = create<ChatState>((set, get) => ({
  rooms: [],
  contacts: [],
  currentRoomId: null,
  byRoom: {},
  isLoadingRooms: false,
  isLoadingMessages: false,
  isLoadingContacts: false,
  error: null,

  fetchRooms: async () => {
    set({ isLoadingRooms: true, error: null })
    try {
      const rooms = await chatApi.getRooms()
      set({ rooms })
    } catch (error: unknown) {
      set({ error: getErrorMessage(error) })
    } finally {
      set({ isLoadingRooms: false })
    }
  },

  fetchContacts: async () => {
    set({ isLoadingContacts: true, error: null })
    try {
      const contacts = await chatApi.getContacts()
      set({ contacts })
    } catch (error: unknown) {
      set({ error: getErrorMessage(error) })
    } finally {
      set({ isLoadingContacts: false })
    }
  },

  openDirect: async (userId) => {
    set({ error: null })
    const room = await chatApi.openDirect(userId)
    set((state) => ({ rooms: upsertRoom(state.rooms, room), currentRoomId: room.id }))
    return room
  },

  setCurrentRoom: (roomId) => set({ currentRoomId: roomId }),

  fetchMessages: async (roomId, options) => {
    const loadMore = options?.loadMore ?? false
    const existing = get().byRoom[roomId] ?? emptyRoomState

    if (loadMore && !existing.hasMore) return

    set({ isLoadingMessages: true, error: null })
    try {
      const page = await chatApi.getMessages(roomId, {
        limit: 30,
        before_id: loadMore ? existing.nextBeforeId ?? undefined : undefined,
      })

      set((state) => {
        const prev = state.byRoom[roomId] ?? emptyRoomState
        const merged = loadMore ? [...page.data, ...prev.messages] : page.data
        return {
          byRoom: {
            ...state.byRoom,
            [roomId]: {
              messages: dedupeById(merged),
              hasMore: page.meta.has_more,
              nextBeforeId: page.meta.next_before_id,
              loaded: true,
            },
          },
        }
      })
    } catch (error: unknown) {
      set({ error: getErrorMessage(error) })
    } finally {
      set({ isLoadingMessages: false })
    }
  },

  sendMessage: async (roomId, payload) => {
    const message = await chatApi.sendMessage(roomId, payload)
    get().applyIncomingMessage(message)
    return message
  },

  editMessage: async (_roomId, messageId, payload) => {
    const updated = await chatApi.editMessage(messageId, payload)
    get().applyUpdatedMessage(updated)
  },

  deleteMessage: async (roomId, messageId) => {
    await chatApi.deleteMessage(messageId)
    set((state) => patchMessage(state, roomId, messageId, (m) => ({
      ...m,
      is_deleted: true,
      text: null,
      attachments: [],
    })))
  },

  markRead: async (roomId, upTo) => {
    await chatApi.markRead(roomId, upTo)
    set((state) => ({
      rooms: state.rooms.map((r) => (r.id === roomId ? { ...r, unread_count: 0 } : r)),
    }))
  },

  setRoomMuted: (roomId, isMuted) => {
    set((state) => ({
      rooms: state.rooms.map((r) => (r.id === roomId ? { ...r, is_muted: isMuted } : r)),
    }))
  },

  updateRoom: (room) => {
    set((state) => ({
      rooms: upsertRoom(state.rooms, room),
    }))
  },

  applyIncomingMessage: (message) => {
    set((state) => {
      const roomState = state.byRoom[message.chat_room_id] ?? emptyRoomState
      const alreadyExists = roomState.messages.some((m) => m.id === message.id)
      const messages = alreadyExists
        ? roomState.messages.map((m) => (m.id === message.id ? message : m))
        : [...roomState.messages, message]

      const isCurrent = state.currentRoomId === message.chat_room_id
      const rooms = state.rooms.map((r) => {
        if (r.id !== message.chat_room_id) return r
        return {
          ...r,
          last_message: message,
          unread_count: isCurrent ? r.unread_count : r.unread_count + 1,
        }
      })

      return {
        byRoom: {
          ...state.byRoom,
          [message.chat_room_id]: { ...roomState, messages: dedupeById(messages), loaded: true },
        },
        rooms,
      }
    })
  },

  applyUpdatedMessage: (message) => {
    set((state) => patchMessage(state, message.chat_room_id, message.id, () => message))
  },

  applyDeletedMessage: (message) => {
    set((state) => patchMessage(state, message.chat_room_id, message.id, (m) => ({
      ...m,
      is_deleted: true,
      text: null,
      attachments: [],
    })))
  },

  applyReadMark: (roomId, userId, lastReadMessageId) => {
    set((state) => ({
      rooms: state.rooms.map((r) => {
        if (r.id !== roomId || !r.members) return r
        return {
          ...r,
          members: r.members.map((member) =>
            member.user_id === userId
              ? { ...member, last_read_message_id: lastReadMessageId }
              : member
          ),
        }
      }),
    }))
  },

  clearError: () => set({ error: null }),
}))

function dedupeById(messages: ChatMessage[]): ChatMessage[] {
  const map = new Map<number, ChatMessage>()
  for (const message of messages) {
    map.set(message.id, message)
  }
  return Array.from(map.values()).sort((a, b) => a.id - b.id)
}

function patchMessage(
  state: ChatState,
  roomId: number,
  messageId: number,
  updater: (message: ChatMessage) => ChatMessage
): Partial<ChatState> {
  const roomState = state.byRoom[roomId]
  if (!roomState) return {}
  return {
    byRoom: {
      ...state.byRoom,
      [roomId]: {
        ...roomState,
        messages: roomState.messages.map((m) => (m.id === messageId ? updater(m) : m)),
      },
    },
  }
}
